import React, { memo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";
import { setSidebarFixed } from "../../../redux/reducers/sidebar";
import {
  SidebarContainer,
  SideBarMenuItem,
  SideBarSubMenuContainer,
  SideBarSubMenuItem,
} from "./style";
import {
  UilAngleDown,
  UilFileCheck,
  UilSetting,
  UilSignin,
  UilSignout,
} from "@iconscout/react-unicons";
import SVG from "../../ui/SVG";
import BoletoIcon from "../../../assets/images/boleto.svg";
import { useNavigationManager } from "../../../hooks/useNavigationManager";
import { getQuerystringParams } from "../../../utils/miscellaneous";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../../../router";
import { getClearedFilters } from "../../../utils/formatters";

const Sidebar: React.FC = memo(() => {
  const isOpen = useSelector((state: RootState) => state.sidebar.isOpen);
  const isFixed = useSelector((state: RootState) => state.sidebar.isFixed);
  const dispatch = useDispatch();
  const [hovered, setHovered] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const { goTo, setQuerystring } = useNavigationManager();
  const location = useLocation();
  const pathname = location.pathname;

  // Sidebar is open if fixed or if open and not hovered, or if hovered and not fixed
  const effectiveOpen = isFixed || isOpen || hovered;

  // Helper to check if a menu item is active
  const isActive = (route: string) => pathname.startsWith(route);

  useEffect(() => {
    const querystring = getQuerystringParams();
    const sidebarFixedParam = querystring.sidebar_fixed;
    if (sidebarFixedParam !== null) {
      const fixedFromQuery = sidebarFixedParam === true;
      if (fixedFromQuery !== isFixed) {
        dispatch(setSidebarFixed(fixedFromQuery));
      }
    }
  }, []);

  return (
    <SidebarContainer
      $isOpen={effectiveOpen}
      $isFixed={isFixed}
      onMouseEnter={() => {
        if (!isFixed) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!isFixed) {
          setHovered(false);
          setSubmenuOpen(false); // Close submenu when mouse leaves sidebar
        }
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "fixed",
          top: 80,
        }}
      >
        <SideBarMenuItem
          onClick={() => {
            const querystring = getClearedFilters();
            setQuerystring(querystring);
            goTo(ROUTES.HOME, querystring);
          }}
          $isOpen={effectiveOpen}
          $isFixed={isFixed}
          className={isActive(ROUTES.INVOICES) ? "active" : ""}
        >
          <UilSignin />
          <span>&nbsp;Notas recebidas</span>
        </SideBarMenuItem>
        <SideBarMenuItem
          onClick={() => {
            const querystring = getClearedFilters();
            setQuerystring(querystring);
            goTo(ROUTES.INVOICES_ISSUED, querystring);
          }}
          $isOpen={effectiveOpen}
          $isFixed={isFixed}
          className={isActive(ROUTES.INVOICES_ISSUED) ? "active" : ""}
        >
          <UilFileCheck />
          <span>&nbsp;Notas emitidas</span>
        </SideBarMenuItem>
        <SideBarMenuItem
          onClick={() => {
            const querystring = getClearedFilters();
            setQuerystring(querystring);
            goTo(ROUTES.PAYMENT_SLIP, querystring);
          }}
          $isOpen={effectiveOpen}
          $isFixed={isFixed}
          className={isActive("/payment-slip") ? "active" : ""}
        >
          <SVG src={BoletoIcon} id="boleto-icon" />
          <span>&nbsp;Boletos</span>
        </SideBarMenuItem>
        <SideBarSubMenuContainer
          $isOpen={effectiveOpen}
          $isFixed={isFixed}
          className={
            isActive("/settings") ? "active" : submenuOpen ? "semi-active" : ""
          }
          onClick={() => setSubmenuOpen((open) => !open)}
        >
          <UilSetting />
          <span
            style={{
              display: "flex",
              alignItems: "center",
              width: "calc(100% - 52px + 8px)",
            }}
          >
            &nbsp;Configurações
            <UilAngleDown
              style={{
                marginLeft: "auto",
                transition: "transform 0.2s",
                transform: submenuOpen ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            />
          </span>
        </SideBarSubMenuContainer>
        {submenuOpen && effectiveOpen && (
          <>
            <SideBarSubMenuItem
              $isOpen={effectiveOpen}
              $isFixed={isFixed}
              className={isActive("/settings/organizations") ? "active" : ""}
              onClick={() => {
                const querystring = getClearedFilters();
                setQuerystring(querystring);
                goTo("/settings/organizations", querystring);
              }}
            >
              <span className="submenu-content">Organizações</span>
            </SideBarSubMenuItem>
            <SideBarSubMenuItem
              $isOpen={effectiveOpen}
              $isFixed={isFixed}
              className={isActive("/settings/users") ? "active" : ""}
              onClick={() => {
                const querystring = getClearedFilters();
                setQuerystring(querystring);
                goTo("/settings/users", querystring);
              }}
            >
              <span className="submenu-content">Usuários</span>
            </SideBarSubMenuItem>
          </>
        )}

        <div style={{ flex: 1 }} />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "fixed",
          top: "calc(100% - 56px)",
        }}
        className="fix-sidebar-container"
      >
        <SideBarMenuItem
          $isOpen={effectiveOpen}
          $isFixed={isFixed}
          className={isFixed ? "active" : ""}
          onClick={() => {
            const querystring = getQuerystringParams();
            querystring.sidebar_fixed = !isFixed;
            setQuerystring(querystring);

            dispatch(setSidebarFixed(!isFixed));
          }}
          aria-label={isFixed ? "Unfix sidebar" : "Fix sidebar"}
        >
          <UilSignout />
          <span>&nbsp;Manter menu aberto</span>
        </SideBarMenuItem>
        <div style={{ flex: 1 }} />
      </div>
    </SidebarContainer>
  );
});

export default Sidebar;
